import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { HttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';
import getNextConfig from 'next/config';
import fetch from 'isomorphic-fetch';

import introspectionQueryResultData from '@g4/g4-graphql-schema/__generated/fragments.json';

import {
  AUTH_HEADER,
  TRANSACTION_HEADER,
  SYSTEM_HEADER,
  extractTokens,
  isUnauthenticated,
  redirectToLogin,
} from '../../utils/authUtils';

import configureStagedTranslations from './stagedTranslations';
import stagedTranslations from '../../../stagedTranslations.json';

// Polyfill fetch() on the server (used by apollo-client)
if (!process.browser) {
  global.fetch = fetch;
}

const nextConfig = getNextConfig();

const apolloLinks = {
  getHttpLink: () => {
    const customFetch = (uri, options) => {
      const { operationName } = JSON.parse(options.body);
      options.headers['X-APOLLO-OPERATION-NAME'] = operationName;
      return fetch(uri, options);
    };
    return new HttpLink({
      uri: !process.browser
        ? nextConfig.serverRuntimeConfig.graphqlEndpoint
        : nextConfig.publicRuntimeConfig.graphqlEndpoint,
      credentials: 'same-origin',
      fetch: customFetch,
    });
  },

  getHeadersLink: serverRequestContext =>
    setContext((_, { headers }) => {
      const customHeaders = {};

      if (process.browser) {
        // we need to get the transaction id from the cookie if we're in the browser
        // currently we choose not to do this server-side since we never want to query for
        // viewer-specific info during the SSR phase
        const tokens = extractTokens();

        tokens.authToken =
          tokens.authToken || nextConfig.publicRuntimeConfig.authToken;

        if (tokens.authToken) {
          customHeaders[AUTH_HEADER] = tokens.authToken;
        }

        if (tokens.transactionId) {
          customHeaders[TRANSACTION_HEADER] = tokens.transactionId;
        }

        if (tokens.systemKey) {
          customHeaders[SYSTEM_HEADER] = tokens.systemKey;
        }
      } else if (serverRequestContext) {
        const {
          req: { headers },
        } = serverRequestContext;
        // varnish will attach this for internal calls
        if (headers[SYSTEM_HEADER]) {
          customHeaders[SYSTEM_HEADER] = headers[SYSTEM_HEADER];
        }
      }

      // return the headers to the context so httpLink can read them
      return {
        headers: {
          ...headers,
          ...customHeaders,
        },
      };
    }),

  getErrorsLink: () =>
    onError(({ graphQLErrors = [], networkError, operation, forward }) => {
      // if (process.env.NODE_ENV === 'development' || 1) {
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Path: ${path}, Location: ${JSON.stringify(
            locations
          )}`
        )
      );
      networkError && console.log(`[Network error]: ${networkError}`);

      //return forward(operation);
    }),

  getAuthErrorLink: () =>
    onError(({ graphQLErrors }) => {
      if (graphQLErrors) {
        const authError = graphQLErrors.find(isUnauthenticated);

        if (authError) {
          redirectToLogin();
        }
      }
    }),
};

// Build a fragment matcher from our schema's introspection data
const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

function createApolloClient(
  initialState,
  introspectionQueryResultData,
  serverRequestContext
) {
  const { resolvers, typeDefs } = configureStagedTranslations(
    stagedTranslations,
    'DesktopBookingPath'
  );

  return new ApolloClient({
    name: nextConfig.publicRuntimeConfig.appName,
    version: nextConfig.publicRuntimeConfig.buildVersion,
    connectToDevTools: process.browser,
    // Disables forceFetch on the server (so queries are only run once)
    ssrMode: !process.browser,
    link: ApolloLink.from([
      apolloLinks.getErrorsLink(),
      apolloLinks.getHeadersLink(serverRequestContext),
      apolloLinks.getAuthErrorLink(),
      apolloLinks.getHttpLink(),
    ]),
    cache: new InMemoryCache({
      fragmentMatcher,
      dataIdFromObject: object => {
        // Both FlightLocation and FlightDestination have id fields but the same
        // id appears across multiple route contexts with different operatedBy values.
        // Apollo would merge them into one cache entry causing the last write to
        // overwrite the correct operatedBy value for other origins/destinations.
        // Returning null stores each object inline with its parent — no merging.
        if (
          object.__typename === 'FlightDestination' ||
          object.__typename === 'FlightLocation'
        ) {
          return null;
        }
        return object.id || object._id || null;
      },
    }).restore(initialState || {}),
    resolvers,
    typeDefs,
  });
}

function getApolloClient(
  initialState,
  introspectionQueryResultData,
  serverRequestContext
) {
  if (process.browser && getApolloClient.Cache) {
    return getApolloClient.Cache;
  }

  // Create a new client for every server-side request, or for
  // the inital client request.
  const apolloClient = createApolloClient(
    initialState,
    introspectionQueryResultData,
    serverRequestContext
  );

  // Reuse client when not in SSR mode.
  if (process.browser) {
    getApolloClient.Cache = apolloClient;
  }

  return apolloClient;
}

getApolloClient.Cache = null;

export default getApolloClient;
